import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { PageSpinner } from "@impero-com/ui";
import { useParams } from "react-router-dom";

import { OrgApplicationBar } from "components/applicationBar/orgApplicationBar";
import { useControlProgramQuery } from "org/control/controlProgram/queries/useControlProgramQuery";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import { controlProgramIdParamSchema } from "org/controlActivities/urlUtils";
import { WebRoute } from "routes";

import { ControlActivitiesScope } from "./consts";
import { ControlActivitiesList } from "./ControlActivitiesList";
import { useControlActivitiesListData } from "./useControlActivitiesListData";
import { useCopyLinkAction } from "./useCopyLinkAction";

/** One control program's activities, with its own persisted view. */
export function ControlActivitiesListForControlProgramPage() {
    const { id: unparsedControlProgramId } = useParams();
    const programIdParseResult = controlProgramIdParamSchema.safeParse(
        unparsedControlProgramId
    );

    if (programIdParseResult.success === false) {
        console.error(
            "Control program id parse failed:",
            unparsedControlProgramId
        );
        return <LoggedInHTTPError code={400} />;
    }

    return (
        <ControlActivitiesListForControlProgramView
            controlProgramId={programIdParseResult.data}
        />
    );
}

interface ControlActivitiesListForControlProgramViewProps {
    controlProgramId: number;
}
function ControlActivitiesListForControlProgramView({
    controlProgramId,
}: ControlActivitiesListForControlProgramViewProps) {
    const { t } = useTranslation();
    const { data: controlProgramDto } =
        useControlProgramQuery(controlProgramId);
    const { controlProgram } = controlProgramDto.content.resource;
    const actions = useCopyLinkAction();

    return (
        <OrgApplicationBar
            title={{
                breadCrumbs: [
                    { label: t("ControlPrograms"), url: WebRoute.CONTROLS },
                    {
                        label: controlProgram.name,
                        href: buildControlProgramViewUrl(controlProgramId),
                    },
                ],
                title: t("ControlResults_Title"),
            }}
            actions={actions}
        >
            <ControlActivitiesListForControlProgram
                controlProgramId={controlProgramId}
            />
        </OrgApplicationBar>
    );
}
function ControlActivitiesListForControlProgram({
    controlProgramId,
}: ControlActivitiesListForControlProgramViewProps) {
    const scope: ControlActivitiesScope = {
        type: "controlProgram",
        controlProgramId,
    };
    const {
        dataParams,
        dateFilters,
        archivedFilter,
        dto,
        tagCategories,
        isPending,
        isTableLoading,
    } = useControlActivitiesListData(scope);

    if (isPending || !dto) {
        return <PageSpinner />;
    }

    return (
        <ControlActivitiesList
            controlResultsDto={dto}
            tagCategories={tagCategories}
            dataParams={dataParams}
            dateFilters={dateFilters}
            archivedFilter={archivedFilter}
            isTableLoading={isTableLoading}
            scope={scope}
        />
    );
}
